import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as RunWithoutSavingIcon } from "../../asserts/logo/RunWithoutSaving.svg";

const MODAL_SHADOW =
  "0px 0px 10px 0px rgba(33, 34, 38, 0.12), 0px 10px 10px 0px rgba(0, 0, 0, 0.24)";

const MODAL_BTN_TEXT_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  textTransform: "none",
  whiteSpace: "nowrap",
};

const MODAL_BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const MODAL_BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const MODAL_PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const MODAL_PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const MODAL_PRIMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

/** Ghost primary — Cancel. */
const MODAL_CANCEL_BTN_SX = {
  ...MODAL_BTN_TEXT_SX,
  boxSizing: "border-box",
  color: "#005BF0",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  px: 2,
  py: 1,
  minHeight: 40,
  borderRadius: "4px",
  boxShadow: MODAL_BTN_SHADOW,
  "& .MuiTouchRipple-root": { display: "none" },
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: MODAL_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: MODAL_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: MODAL_BTN_INSET,
  },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    color: "#BBBBBB !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

/** Solid primary — Confirm. */
const MODAL_CONFIRM_BTN_SX = {
  ...MODAL_BTN_TEXT_SX,
  boxSizing: "border-box",
  color: "#FFFFFF",
  px: 2,
  py: 1,
  minHeight: 40,
  borderRadius: "4px",
  border: "1px solid transparent",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: MODAL_PRIMARY_BG,
  boxShadow: MODAL_BTN_SHADOW,
  "& .MuiTouchRipple-root": { display: "none" },
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: MODAL_PRIMARY_BG_HOVER,
    boxShadow: MODAL_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${MODAL_PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: MODAL_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${MODAL_PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${MODAL_PRIMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: MODAL_BTN_INSET,
  },
  "&.Mui-disabled": {
    color: "#BBBBBB !important",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    boxShadow: MODAL_BTN_SHADOW,
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

/** Figma 5478:26440 / 5478:26596 — confirm before Gate 2 custom test case dry run. */
export default function RunWithoutSavingDialog({ open, onClose, onConfirm, submitting = false }) {
  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      aria-labelledby="run-without-saving-dialog-title"
      aria-describedby="run-without-saving-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: "4px",
          boxSizing: "border-box",
          width: { xs: "calc(100% - 32px)", sm: 600 },
          maxWidth: 600,
          mx: 2,
          pt: "40px",
          pr: { xs: 3, sm: "64px" },
          pb: "40px",
          pl: { xs: 3, sm: "64px" },
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          boxShadow: MODAL_SHADOW,
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.35)" } },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        disabled={submitting}
        sx={{ position: "absolute", top: 8, right: 8, color: "#555555" }}
      >
        <CloseIcon sx={{ fontSize: 24 }} />
      </IconButton>

      <Box aria-hidden sx={{ flexShrink: 0, lineHeight: 0, display: "flex" }}>
        <RunWithoutSavingIcon style={{ width: 43, height: 43, display: "block" }} />
      </Box>

      <Typography
        id="run-without-saving-dialog-title"
        component="h2"
        sx={{
          fontFamily: "Lato, sans-serif",
          fontWeight: 900,
          fontSize: 20,
          lineHeight: "27px",
          color: "#000000",
          textAlign: "center",
          m: 0,
        }}
      >
        Run without saving?
      </Typography>

      <Typography
        id="run-without-saving-dialog-description"
        component="p"
        sx={{
          fontFamily: "Lato, sans-serif",
          fontWeight: 400,
          fontSize: 14,
          lineHeight: "21px",
          color: "#555555",
          textAlign: "center",
          m: 0,
          maxWidth: 472,
        }}
      >
        This will execute the test case once and show results, but it won&apos;t be saved to your test
        suite.
        <br />
        Use Run Once to validate prompts before committing a test case.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          pt: 0.5,
          width: "100%",
        }}
      >
        <Button
          disableElevation
          disableRipple
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
          sx={MODAL_CANCEL_BTN_SX}
        >
          Cancel
        </Button>
        <Button
          disableElevation
          disableRipple
          variant="contained"
          onClick={onConfirm}
          disabled={submitting}
          sx={MODAL_CONFIRM_BTN_SX}
        >
          Confirm
        </Button>
      </Box>
    </Dialog>
  );
}
